import { ShipmentDirectionEnum } from '@core/models/shipment-type';

export interface ShipperProperties {
  // From user input
  quantity?: number;

  // From get-available-packages API
  enumValue: string;
  packageType: PackageTypeEnum;
  outerDimension: {
    length: number;
    width: number;
    height: number;
  };
  payloadDimension: {
    length: number;
    width: number;
    height: number;
  };

  // From CMS API
  name?: string;
  documentId?: string;
  capacity?: string | null;
  capacityDescription?: string | null;
  productBoxFieldActive?: boolean;
  productBoxIncluded?: boolean;
  dryIceCapacity?: number | null;
  weightWithoutPayload?: string | null;
  weightInLBS?: number | null;
  images?: ShipperImage[];
  capacityPopover?: string | null;
  usageGuide?: ShipperDocument | null;
  validationGuide?: ShipperDocument | null;
  payloadDimensions?: string | null;
  outerDimensions?: string | null;

  // From CMS, lazy fetched
  productBoxPopover?: ShipperImage | null;
}

export interface ShipperCMSData {
  documentId: string;
  capacity: string | null;
  capacityDescription: string | null;
  productBoxFieldActive: boolean;
  productBoxIncluded: boolean;
  dryIceCapacity: string | null;
  weightWithoutPayload: string | null;
  images: ShipperImage[];
  capacityPopover: string | null;
  usageGuide: ShipperDocument | null;
  validationGuide: ShipperDocument | null;
}

export interface ShipperImage {
  id: string;
  url: string;
  alt: string;
  width: number;
  height: number;
}

export interface ShipperDocument {
  id: string;
  url: string;
  name: string;
}

export interface DangerousGood {
  id: number;
  name: string;
  codeType: string;
  code: string;
  class: string;
  packagingGroup: string;
  weight: number;
  weightUnit: string;
  quantity: number;
  packageType: string;
  description: string;
  packageTypes: string[];
  selected: boolean;
}

export interface Options {
  temperatureControl: {
    isTempControlled: boolean;
    tempRange: TemperatureRangeEnum | null | undefined;
    customTempDescription: string;
    needValidatedShippers: boolean;
    shipper?: ShipperProperties;
  };
  dangerousGoodsSelection: {
    dangerousGoods: DangerousGoodsEnum[];
    lithiumBatteries: LithiumBatteriesEnum[];
    supplyDryIce: boolean;
    otherDangerousGoods: DangerousGood[];
  };
  trackerSelection: {
    trackerType: TrackerTypeEnum | null;
    otherTrackerDescription: string;
  };
  xraySuitable: boolean;
}

export interface PackageFormValue {
  packageContent?: PackageContentsEnum;
  packageQuantity?: number;
  packageType?: PackageTypeEnum;
  packageTemplate?: string;
  packageTemplateName?: string;
  dimensions?: {
    length?: number;
    width?: number;
    height?: number;
  };
  outerDimension?: {
    length?: number;
    width?: number;
    height?: number;
  };
  payloadDimension?: {
    length?: number;
    width?: number;
    height?: number;
  };
  dryIceWeight?: number;
  totalWeight?: number;
  boxWeight?: number;
}

export interface PackageForm {
  value?: PackageFormValue;
  valid?: boolean;
}

export interface Package {
  id: string;
  options?: Options;
  form?: PackageForm;
  availableShippers: AvailableShipperResponseDTO[];
  shipperCmsDataMap?: Map<string, ShipperCMSData>;
  availableShippersLoading?: boolean;
}

export interface ShipmentContents {
  packages: Package[];
  supplyDeliveryTime: SupplyDeliveryTimeEnum;
  shipmentDescription: string;
}

export interface ReviewPackage {
  id: string;
  quantity: string;
  typeAndTemplate: string;
  dimensions: string;
  weight: string;
  dryIceWeightInKg: string | null;
  contentType: string;
  isTempControlOrDg: boolean;
  tempControlText?: string;
  shippersText?: string;
  xrayText?: string;
  trackerText?: string;
  dangerousGoodsText?: string;
  needMercurySupply?: boolean;
}

export interface AvailableShippersRequestDTO {
  originCity: string;
  coldChainShipmentType: ShipmentDirectionEnum;
  temperatureRange: TemperatureRangeEnum;
  templateType: PackageTypeEnum;
  mercurySupply: boolean;
  temperatureControlled: boolean;
}

export interface HybridAvailablePackagesRequestDTO {
  originCity: string;
  shipmentType: ShipmentDirectionEnum;
  temperatureRange?: TemperatureRangeEnum;
  templateType: PackageTypeEnum;
  mercurySupply: boolean;
  temperatureControlled: boolean;
}

export interface AvailableShipperResponseDTO {
  id: number;
  createdDate?: string | null;
  createdBy?: string | null;
  lastModifiedDate?: string | null;
  lastModifiedBy?: string | null;
  status: TemplateActivityEnum;
  name: string;
  enumValue: string;
  description: string | null;
  validationHours: number | null;
  maxWeightInLBS: number | null;
  type: PackageTypeEnum;
  outerDimension: {
    length: number;
    width: number;
    height: number;
  } | null;
  payloadDimension: {
    length: number;
    width: number;
    height: number;
  } | null;
}

export interface ShipmentCreationPackageRequestDTO {
  contentType: PackageContentsEnum;
  quantity: number;
  dimension: {
    length: number;
    width: number;
    height: number;
  } | null;
  outerDimension: {
    length: number;
    width: number;
    height: number;
  } | null;
  payloadDimension: {
    length: number;
    width: number;
    height: number;
  } | null;
  totalWeightInLBS: number | null;
  temperatureControlled: boolean;
  tsaXray: boolean;
  trackerType: TrackerTypeEnum;
  trackerDetails: string | null;
  temperatureRangeType: TemperatureRangeEnum | null | undefined;
  temperatureRangeDetails: string | null;
  needMercurySupply: boolean;
  packageTemplateValue: string | null;
  packageTemplateName: string | null;
  packageTemplateType: PackageTypeEnum | undefined;
  dangerousGoods: DangerousGoodsRequestEnum[];
  dryIceWeightInKg: number | null;
  supplyDeliveryType: SupplyDeliveryTimeEnum;
}

export interface ShipmentCreationPackageResponseDTO {
  id: number;
  createdDate: string | null;
  createdBy: string | null;
  lastModifiedDate: string | null;
  lastModifiedBy: string | null;
  status: TemplateActivityEnum;
  shipmentIdentifier: string;
  type: PackageContentsEnum;
  quantity: number;
  dimension: {
    length: number;
    width: number;
    height: number;
  } | null;
  outerDimension: {
    length: number;
    width: number;
    height: number;
  } | null;
  payloadDimension: {
    length: number;
    width: number;
    height: number;
  } | null;
  totalWeightInLBS: number;
  productWeightInLBS?: number | null;
  temperatureControlled: boolean;
  tsaXray: boolean;
  trackerType: TrackerTypeEnum;
  trackerDetails: string | null;
  temperatureRangeType: TemperatureRangeEnum | null | undefined;
  temperatureRangeDetails: string | null;
  needMercurySupply: boolean;
  dangerousGoods: DangerousGoodsRequestEnum[];
  // packageTemplate: AvailableShipperResponseDTO | null;
  packageTemplateValue: string;
  packageTemplateName: string;
  packageTemplateType: PackageTypeEnum;
  dryIceWeightInKg: number | null;
  supplyDeliveryType: SupplyDeliveryTimeEnum;
}

export enum PackageTypeEnum {
  BOX = 'BOX',
  LETTER = 'LETTER',
  UNPACKAGE = 'UNPACKAGE',
  CONTAINER = 'CONTAINER',
  PALLET = 'PALLET',
  CRATE = 'CRATE',
  OTHER = 'OTHER',
}

export enum TemperatureRangeEnum {
  CRT = 'CRT',
  TEMP_2C_8C = 'TEMP_2C_8C',
  MINUS_20 = 'MINUS_20',
  DRY_ICE = 'DRY_ICE',
  CRYOGENIC = 'CRYOGENIC',
  OTHER = 'OTHER',
}

export enum TemplateActivityEnum {
  ACTIVE = 'ACTIVE',
  PASSIVE = 'PASSIVE',
}

export enum TrackerTypeEnum {
  NONE = 'NONE',
  LIVE_GPS = 'LIVE_GPS',
  SENSITECH_GPS = 'SENSITECH_GPS',
  OTHER = 'OTHER',
}

export enum DangerousGoodsEnum {
  DRY_ICE = 'DRY_ICE',
  BIOLOGICAL_SUBSTANCE = 'BIOLOGICAL_SUBSTANCE',
  LITHIUM_ION_BATTERIES = 'LITHIUM_ION_BATTERIES',
  OTHER = 'OTHER',
}

export enum LithiumBatteriesEnum {
  METAL_CONTAINED = 'METAL_CONTAINED',
  METAL_PACKED = 'METAL_PACKED',
  ION_CONTAINED = 'ION_CONTAINED',
  ION_PACKED = 'ION_PACKED',
}

export enum DangerousGoodsRequestEnum {
  DRY_ICE_WITH_SUPPLY = 'DRY_ICE_WITH_SUPPLY',
  DRY_ICE_WITHOUT_SUPPLY = 'DRY_ICE_WITHOUT_SUPPLY',
  CATEGORY_B_BIO_SUBSTANCE = 'CATEGORY_B_BIO_SUBSTANCE',
  LITHIUM_METAL_CONTAINED = 'LITHIUM_METAL_CONTAINED',
  LITHIUM_METAL_PACKED = 'LITHIUM_METAL_PACKED',
  LITHIUM_ION_CONTAINED = 'LITHIUM_ION_CONTAINED',
  LITHIUM_ION_PACKED = 'LITHIUM_ION_PACKED',
  OTHER = 'OTHER',
}

export enum PackageContentsEnum {
  GENERAL = 'GENERAL',
  TEMPERATURE_OR_DG = 'TEMPERATURE_OR_DG',
  DOCUMENT = 'DOCUMENT',
  OTHER = 'OTHER',
}

export enum ShipmentContentsFormContextEnum {
  SCC = 'SCC',
  HYBRID = 'HYBRID',
}

export enum SupplyDeliveryTimeEnum {
  WITH_PICKUP = 'WITH_PICKUP',
  BEFORE_PICKUP = 'BEFORE_PICKUP',
}
